import {
  ParlorHeadSection,
  BTypeMainHead,
  BTypeCardView,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ATypeFlowerSummary,
  GOI_WEB_URL,
  CTypeBreadCrumb,
  HLine,
  colors,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";
import { ParlorDetailProps, ATypeTabs } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        id: String(parlor.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      const descParts = [
        `${parlor.companyname} 근조화환·조화 배송 안내입니다`,
        `고이장례연구소에서 e하늘 장사정보 기반으로 안내드립니다`,
        `주소 : ${parlor.address}`,
        `2시간 이내 당일 배송`,
        `삼가 고인의 명복을 빕니다`,
      ].filter(Boolean);
      description = descParts.join(' | ');
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 화환, ${parlor?.companyname} 화환 배송, ${parlor?.companyname} 근조화환, ${parlor?.companyname} 꽃, ${parlor?.companyname} 꽃배달, ${parlor?.companyname} 꽃집, 꽃배달, 꽃, 꽃집, 화환`,
      pageOgTitle: `${parlor?.companyname} 근조화환 배송 (${district}) | 강원 장례정보 [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export default function flower({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionParlors = parlors.filter((res) => targetSidos.includes(res.sido));
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu)
  );

  const carouselItems = regionParlors.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gangwon-portal.co.kr/gangwon-parlor-places/${res.id}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionParlors.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>
      <CTypeBreadCrumb
        firstItem={{
          text: `장례식장 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "gangwon",
            path: "prlr_flower",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `${sidoName} 장례식장`,
          href: `/gangwon-parlors/${sidoName}도 의 장례식장들/`,
          event: {
            domain: "gangwon",
            path: "prlr_flower",
            trigger_and_target: "br_crumb_sido_prlr_list",
          },
        }}
        thirdItem={{
          text: `${sigunguName} 장례식장`,
          href: `/gangwon-parlors/${sidoName}도-${sigunguName} 의 장례식장들/`,
          event: {
            domain: "gangwon",
            path: "prlr_flower",
            trigger_and_target: "br_crumb_sigungu_prlr_list",
          },
        }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "prlr_flower",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ParlorHeadSection
        component={
          <>
            <BTypeMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              mainColor={regionColor}
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname} 근조화환`}
              managed_type={defaultParlor.managed_type}
            />
            <Box width="100%" className="subtitle_14_sb gray900" padding="0 20px" margin="12px 0" textAlign="center">
              근조화환 배송 서비스 (고이장례연구소)
            </Box>
            <Box padding="0 20px" marginBottom="32px">
              <HLine height="1px" color={colors.gray900} />
            </Box>
          </>
        }
      />
      <Stack style={{ background: "white" }}>
        <ATypeTabs
          mainColor={regionColor}
          href={`gangwon-parlor-places/${defaultParlor.id}`}
          selectTab="화환 보내기"
          defaultParlor={defaultParlor}
          event={{
            domain: "gangwon",
            path: "prlr_flower",
            trigger_and_target: "AType_tab",
          }}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeFlowerSummary
          defaultParlor={defaultParlor}
          event={{
            domain: "gangwon",
            path: "prlr_flower",
            trigger_and_target: "CTA_uv_wreath_req",
          }}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <DTypeParlorList
          binsoAveragePrice
          sido={defaultParlor.sido}
          sigungu={String(defaultParlor.sigungu)}
          currentParlorId={defaultParlor.id}

          currentLat={Number((defaultParlor as any).latitude)}

          currentLng={Number((defaultParlor as any).longitude)}
          title={`${sidoName}의 다른 장례식장`}
          CardViewType={(res) => (
            <BTypeCardView
              descriptions={[
                ...(res.distanceLabel ? [`${defaultParlor.companyname}에서 ${res.distanceLabel}`] : []),
                `빈소 이용료: ${res.binsos[0].charge.toLocaleString()}원부터`,
              ]}
              mainColor={regionColor}
              key={res.companyname}
              name={res.companyname}
              href={`/gangwon-parlor-places/${res.id}/`}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "gangwon",
                path: "prlr_flower",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
